import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import TextCard from './TextCard';

const HomeContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const HeroSection = styled.section`
  text-align: center;
  padding: 3rem 0;
  margin-bottom: 3rem;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const Section = styled.section`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  color: white;
  text-align: center;
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
`;

const CategoryCard = styled(Link)`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  text-decoration: none;
  color: inherit;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  text-align: center;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

const CategoryIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const CategoryName = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 0.5rem;
  color: ${props => props.color};
`;

const CategoryDescription = styled.p`
  color: #666;
  font-size: 0.9rem;
`;

const RecommendationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const QuickActionButton = styled.button`
  background: linear-gradient(45deg, #FF6B6B, #4ECDC4);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 0 0.5rem;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 2rem;
`;

const getCategoryDescription = (categoryId) => {
  const descriptions = {
    funny: '让人捧腹大笑的幽默文字',
    warm: '温暖人心的暖心话语',
    cute: '甜腻可爱的撒娇语句',
    work: '职场专用的幽默表达',
    festival: '节日庆祝的祝福语',
    daily: '日常生活的贴心提醒',
    love: '浪漫甜蜜的恋爱话语',
    friendship: '友谊深厚的暖心话语'
  };
  return descriptions[categoryId] || '';
};

const HomePage = ({
  categories,
  recommendations,
  onToggleFavorite,
  isFavorite,
  onCategoryClick
}) => {
  const handleRandomRecommendation = () => {
    window.location.reload(); // 简单的随机刷新推荐
  };

  return (
    <HomeContainer className="fade-in">
      <HeroSection>
        <HeroTitle>👋 拍一拍文字</HeroTitle>
        <HeroSubtitle>
          发现有趣、温暖、可爱的拍一拍短句<br />
          让你的聊天更加生动有趣
        </HeroSubtitle>
        <ActionButtons>
          <QuickActionButton onClick={handleRandomRecommendation}>
            🎲 随机推荐
          </QuickActionButton>
          <QuickActionButton as={Link} to="/favorites">
            ❤️ 我的收藏
          </QuickActionButton>
        </ActionButtons>
      </HeroSection>

      <Section>
        <SectionTitle>📚 浏览分类</SectionTitle>
        <CategoryGrid className="grid-4">
          {categories.map(category => (
            <CategoryCard
              key={category.id}
              to={`/category/${category.id}`}
              className="category-item"
              onClick={() => onCategoryClick(category.id)}
            >
              <CategoryIcon>{category.icon}</CategoryIcon>
              <CategoryName color={category.color}>
                {category.name}
              </CategoryName>
              <CategoryDescription>
                {getCategoryDescription(category.id)}
              </CategoryDescription>
            </CategoryCard>
          ))}
        </CategoryGrid>
      </Section>

      {recommendations.length > 0 && (
        <Section>
          <SectionTitle>✨ 为你推荐</SectionTitle>
          <RecommendationGrid className="grid-2">
            {recommendations.map((item, index) => {
              const category = categories.find(cat => cat.id === item.category);
              return (
                <TextCard
                  key={index}
                  text={item.text}
                  category={category}
                  onToggleFavorite={() => onToggleFavorite(item.text, item.category)}
                  isFavorite={isFavorite(item.text)}
                  recommendation={item.reason}
                />
              );
            })}
          </RecommendationGrid>
        </Section>
      )}
    </HomeContainer>
  );
};

export default HomePage;
